import type { FaultLink } from "../services/calculationRun";
import { formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";
import { CASE_LABELS, FAULT_TYPE_LABELS } from "./starterCoordinationDraft";

// The fault run a stored cable run was sized on (SLD-b3, A28 (e)) in the shared UX-1
// contracts, beside the SLD link panel: the banner names the run and its revision, the line
// gives the Ik'' the engine used with the exact decimal in its tooltip (A12) and the run's
// bus, case and fault type, and a REVIEW outcome lists one review-tone card per warning.
// Nothing is shown for a study that named no fault run (the panel is not rendered at all).
//
// The words depend on what the study took from the run: the short-circuit current (a cable,
// a starter, a breaker, a capacitor bank) or the external earth-fault loop impedance Ze of
// EOS-05d, read from the minimum single-phase-to-earth case (A33, D3). The wording is a
// prop; the default is the short-circuit current every earlier page used.

export type FaultLinkWording = {
  // What the study took from the run; starts the banner sentence.
  subject: string;
  // The sentence that ends the detail line of a VALID link.
  validNote: string;
  // The readable name of each warning code of this link.
  codeLabels: Record<string, string>;
};

export const FAULT_LINK_CODE_LABELS: Record<string, string> = {
  FAULT_RUN_NOT_MAXIMUM_THREE_PHASE: "Not the maximum three-phase fault",
  FAULT_RUN_BUS_MISMATCH: "Fault run of another bus",
};

export const SHORT_CIRCUIT_CURRENT_WORDING: FaultLinkWording = {
  subject: "Short-circuit current",
  validNote: "the run is the maximum three-phase fault of the feeder's bus.",
  codeLabels: FAULT_LINK_CODE_LABELS,
};

export const EARTH_FAULT_LOOP_WORDING: FaultLinkWording = {
  subject: "External loop impedance",
  validNote: "the run is the minimum single-phase-to-earth fault of the board's bus.",
  codeLabels: {
    FAULT_RUN_NOT_MINIMUM_EARTH_FAULT: "Not the minimum single-phase-to-earth fault",
    FAULT_RUN_BUS_MISMATCH: "Fault run of another bus",
  },
};

export function faultLinkStatusText(
  link: FaultLink,
  wording: FaultLinkWording = SHORT_CIRCUIT_CURRENT_WORDING,
): string {
  const run = `${link.calculation_key} rev ${link.revision_number}`;
  return link.status === "VALID"
    ? `${wording.subject} from fault run ${run}: valid`
    : `${wording.subject} from fault run ${run}: review required`;
}

/** "Ik'' 35.22 kA · bus MSB-01 · Maximum · Three-phase" */
export function faultLinkDetailText(link: FaultLink): string {
  const current = formatQuantity(link.prospective_current_ka, "kA").display;
  const words = `${(CASE_LABELS as Record<string, string>)[link.calculation_case] ?? link.calculation_case} · ${
    (FAULT_TYPE_LABELS as Record<string, string>)[link.fault_type] ?? link.fault_type
  }`;
  return `Ik'' ${current} · bus ${link.fault_bus_code} · ${words}`;
}

type FaultLinkPanelProps = {
  link: FaultLink | null | undefined;
  wording?: FaultLinkWording;
};

export function FaultLinkPanel({ link, wording = SHORT_CIRCUIT_CURRENT_WORDING }: FaultLinkPanelProps) {
  if (!link) {
    return null;
  }
  const valid = link.status === "VALID";
  return (
    <section aria-label="Fault current source" data-summary-tone={valid ? "pass" : "warning"}>
      <header>
        <p data-summary-status={link.status}>
          <StatusIcon tone={valid ? "pass" : "review"} size={20} />
          {faultLinkStatusText(link, wording)}
        </p>
        <p
          data-fault-link-current={link.fault_run_id}
          title={formatQuantity(link.prospective_current_ka, "kA").exact ?? ""}
        >
          {faultLinkDetailText(link)}
          {valid ? ` · ${wording.validNote}` : ` · ${link.warnings.length} item(s) to review.`}
        </p>
      </header>
      {link.warnings.length > 0 ? (
        <ul data-warning-count={link.warnings.length}>
          {link.warnings.map((warning, index) => (
            <li
              key={`${warning.code}-${index}`}
              data-warning-code={warning.code}
              data-warning-tone="review"
            >
              <strong>
                <StatusIcon tone="review" size={16} />
                {wording.codeLabels[warning.code] ?? warning.code}
              </strong>
              {": "}
              <span>{warning.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export default FaultLinkPanel;
